import { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { DemoForm } from "@/components/site/DemoForm";
import { Reveal, Kicker } from "@/components/site/primitives";
import { getCaseStudy } from "@/resourcesData";

export default function CaseStudy() {
  const { slug } = useParams();
  const [demoOpen, setDemoOpen] = useState(false);
  const data = getCaseStudy(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <Navigate to="/resources" replace />;

  return (
    <div data-testid={`case-study-${slug}`}>
      <Navbar onBookDemo={() => setDemoOpen(true)} />
      <main className="pt-28">
        <section className="relative overflow-hidden border-b border-slate-200">
          <div className="absolute inset-0 -z-10">
            <img src={data.image} alt={data.company} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[#0B1240]/88" />
          </div>
          <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
            <Link to="/resources" data-testid="case-back" className="group mb-10 inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-white/70 transition-colors hover:text-volt">
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> All stories
            </Link>
            <div className="font-mono-p text-xs uppercase tracking-[0.25em] text-volt">{data.industry} · {data.company}</div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[0.95] text-white sm:text-6xl">
              {data.headline}
            </h1>
            <div className="mt-10 flex flex-wrap gap-10">
              {data.metrics.map((m) => (
                <div key={m.k}>
                  <div className="font-display text-4xl font-black text-volt">{m.v}</div>
                  <div className="mt-1 font-mono-p text-[11px] uppercase tracking-widest text-white/60">{m.k}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <Kicker>The challenge</Kicker>
            <p className="mt-6 text-xl leading-relaxed text-slate-700">{data.challenge}</p>
          </Reveal>

          <Reveal className="mt-16">
            <Kicker>What we did</Kicker>
            <ul className="mt-6 space-y-4">
              {data.solution.map((s) => (
                <li key={s} className="flex items-start gap-3 text-lg text-slate-600">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-volt" strokeWidth={2.4} />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-16">
            <div className="rounded-lg border-l-4 border-volt bg-[#F4F6FB] p-8">
              <p className="font-display text-2xl font-medium leading-snug text-slate-900">"{data.quote}"</p>
              <div className="mt-5 font-display font-bold text-slate-900">{data.author}</div>
              <div className="font-mono-p text-xs text-slate-400">{data.authorRole}</div>
            </div>
          </Reveal>

          <Reveal className="mt-16">
            <Kicker>The results</Kicker>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {data.results.map((r) => (
                <div key={r} className="rounded-lg border border-slate-200 bg-white p-5 text-sm leading-relaxed text-slate-600">
                  <Check className="mb-3 h-5 w-5 text-volt" strokeWidth={2.4} />
                  {r}
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="bg-volt text-white">
          <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10">
            <h2 className="max-w-2xl font-display text-4xl font-black leading-[0.95] sm:text-5xl">
              Want results like {data.company}?
            </h2>
            <button
              data-testid="case-cta-book-demo"
              onClick={() => setDemoOpen(true)}
              className="group flex shrink-0 items-center gap-3 bg-white px-8 py-5 font-display text-lg font-bold text-volt transition-transform hover:-translate-y-1"
            >
              Book a Demo
              <ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2.2} />
            </button>
          </div>
        </section>
      </main>
      <Footer />
      <DemoForm open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
